/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import error from '../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  EXECUTION: 'execution',
  SUCCESS: 'success',
  DTC_DETECTION_FAILED: 'dtcDetectionFailed',
  VIBRATION_FAILED: 'vibrationFailed',
  FAILED: 'failed',
};

export default {
  name: 'requirement057',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      isCallExit: false,
      isNotExecExit: false,
      modal_requirement_057: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.DTC_DETECTION_FAILED:
        case PHASE.VIBRATION_FAILED:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.EXECUTION:
        case PHASE.START:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_057 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_057 = false;
    },

    /** OKボタン押下 */
    async exitProcess() {
      // Wizardを閉じる
      this.closeWizard();
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * API呼び出し
     * 
     * @param {String} type APIの種類
     * @returns pollingWspApiの実行結果
     */
    async callApi(type) {

      const token = session.getToken();

      return await individualWorkSupportUtil.pollingWspApi(
        this, this.netAppId,
        this.systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: type
        },
        // エラー発生のコールバック
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
      );
    },

    /** 実行処理 */
    async execute() {
      try {
        // スピナー開始
        this.isLoading = true;

        // 実行状態に遷移
        this.phase = PHASE.EXECUTION;

        // 実行処理(セッション遷移～ルーチン実行・停止)API呼び出し
        const executeResponse = await this.callApi('execute');

        if (executeResponse) {
          const status = executeResponse.data.status;
          const asyncCode = executeResponse.data.async_code;

          // executeが成功
          if (status === config.STATUS_WSP_COMPLETED) {
            // 終了処理API呼び出し
            const exitResponse = await this.callApi('exit');
            // exitが正常終了の場合
            if (exitResponse && exitResponse.data.status === config.STATUS_WSP_COMPLETED) {
              this.phase = PHASE.SUCCESS;
              return;
            } else {
              this.showUnexpectedError();
              return;
            }
          }

          // 異常終了の場合
          if (asyncCode === error.WSP_ABORT_ERROR) {
            // 終了処理API呼び出し
            const exitResponse = await this.callApi('exit');
            // exitが正常終了の場合
            if (exitResponse && exitResponse.data.status === config.STATUS_WSP_COMPLETED) {
              // 異常終了画面に遷移
              this.phase = PHASE.VIBRATION_FAILED;
              return;
            } else {
              this.showUnexpectedError();
              return;
            }
          }

          // DTC検出エラーの場合
          if (asyncCode === error.WSP_DETECT_DTC_ERROR) {
            // 終了処理API呼び出し
            const exitResponse = await this.callApi('exit');
            // exitが正常終了の場合
            if (exitResponse && exitResponse.data.status === config.STATUS_WSP_COMPLETED) {
              // DTC検出エラー画面に遷移
              this.phase = PHASE.DTC_DETECTION_FAILED;
              return;
            } else {
              this.showUnexpectedError();
              return;
            }
          }
        }

        // exitを実行していないフラグ
        this.isNotExecExit = true;
        // どれでもない場合は共通エラー
        this.showUnexpectedError();
      } finally {
        // スピナー終了
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // exitを実行していなければ実行
      if (this.isNotExecExit) {
        // 終了処理API呼び出し
        const token = session.getToken();

        individualWorkSupportUtil.pollingWspApi(
          this, this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // 共通エラーではエラー発生時でも何もしない
        );
      }
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },
  },

  async mounted() {
    this.init();
  },
};